import { createFileRoute, Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '@/api/http';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { BookSessionModal } from '@/components/shared/book-session-modal';

export const Route = createFileRoute('/dashboard/clientes')({ component: ClientesPage });

type Row =
  | { kind: 'registered'; id: string; name: string; email: string; image: string | null; phone: string | null }
  | { kind: 'invite'; invId: string; token: string; name: string; email: string; phone: string | null; status: string; expiresAt: string };

function ClientesPage() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['my-clients'],
    queryFn: () =>
      api.get('invitations/my-clients').json<{
        registered: any[];
        invitations: any[];
        invClientProfileId: Record<string, string>;
      }>(),
  });

  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ name: '', email: '', phone: '', sendEmail: true });
  const [bookingFor, setBookingFor] = useState<{ id: string; name: string } | null>(null);
  const [copiedToken, setCopiedToken] = useState<string | null>(null);

  // Pra alimentar o BookSessionModal com o therapistId + janela O-09
  const { data: myProfile } = useQuery({
    queryKey: ['my-therapist-profile'],
    queryFn: () => api.get('therapists/me/profile').json<any>(),
  });

  const createInvite = useMutation({
    mutationFn: (body: any) => api.post('invitations', { json: body }).json<any>(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-clients'] });
      setAddForm({ name: '', email: '', phone: '', sendEmail: true });
      setShowAdd(false);
    },
  });

  const removeInvite = useMutation({
    mutationFn: (id: string) => api.delete(`invitations/${id}`).json<any>(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['my-clients'] }),
  });

  const [resendNotice, setResendNotice] = useState<{ id: string; ok: boolean } | null>(null);
  const resendEmail = useMutation({
    mutationFn: (id: string) => api.post(`invitations/${id}/resend-email`).json<any>(),
    onSuccess: (_d, id) => {
      setResendNotice({ id, ok: true });
      setTimeout(() => setResendNotice(null), 3000);
    },
    onError: (_e, id) => {
      setResendNotice({ id, ok: false });
      setTimeout(() => setResendNotice(null), 4000);
    },
  });

  const registered = data?.registered ?? [];
  const invitations = data?.invitations ?? [];
  const invClientProfileId = data?.invClientProfileId ?? {};
  const total = registered.length + invitations.length;

  const rows: Row[] = [
    ...registered.map((c: any): Row => ({
      kind: 'registered', id: c.id,
      name: c.user?.name ?? '?',
      email: c.user?.email ?? '',
      // Prioriza avatarUrl (upload S3) sobre user.image (Google); ignora
      // data URI legado (base64 de baixa-res — limpo no data-fix).
      image: (c.avatarUrl && !c.avatarUrl.startsWith('data:') ? c.avatarUrl : null) ?? c.user?.image ?? null,
      phone: c.phone ?? null,
    })),
    ...invitations.map((inv: any): Row => ({
      kind: 'invite', invId: inv.id, token: inv.token,
      name: inv.name, email: inv.email, phone: inv.phone ?? null,
      status: inv.status, expiresAt: inv.expiresAt,
    })),
  ];

  return (
    <div className="max-w-4xl space-y-6">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900">Meus clientes</h1>
          <p className="text-slate-500 text-sm mt-1">{isLoading ? '...' : `${total} cliente${total !== 1 ? 's' : ''}`}</p>
        </div>
        <Button onClick={() => setShowAdd((v) => !v)} className="bg-coral-400 hover:bg-coral-500 text-white">
          {showAdd ? 'Cancelar' : '+ Adicionar cliente'}
        </Button>
      </div>

      {showAdd && (
        <Card>
          <CardContent className="p-5 space-y-4">
            <p className="text-sm font-semibold text-slate-700">Novo pré-cadastro</p>
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Nome completo *">
                <input value={addForm.name} onChange={(e) => setAddForm((f) => ({ ...f, name: e.target.value }))} placeholder="Maria da Silva" className="input" />
              </Field>
              <Field label="E-mail *">
                <input type="email" value={addForm.email} onChange={(e) => setAddForm((f) => ({ ...f, email: e.target.value }))} placeholder="maria@email.com" className="input" />
              </Field>
              <Field label="Telefone">
                <input value={addForm.phone} onChange={(e) => setAddForm((f) => ({ ...f, phone: e.target.value }))} placeholder="(11) 99999-9999" className="input" />
              </Field>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <input
                type="checkbox"
                checked={addForm.sendEmail}
                onChange={(e) => setAddForm((f) => ({ ...f, sendEmail: e.target.checked }))}
                className="accent-teal-500"
              />
              Enviar e-mail de convite agora
            </label>
            <Button
              onClick={() => createInvite.mutate({
                name: addForm.name.trim(),
                email: addForm.email.trim(),
                phone: addForm.phone.trim() || undefined,
                sendEmail: addForm.sendEmail,
              })}
              disabled={createInvite.isPending || !addForm.name.trim() || !addForm.email.trim()}
              className="bg-teal-500 hover:bg-teal-600 text-white"
            >
              {createInvite.isPending ? 'Salvando...' : 'Salvar cliente'}
            </Button>
            {createInvite.isError && <p className="text-sm text-red-600">Erro ao criar convite.</p>}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          {isLoading ? (
            <div className="p-12 text-center text-sm text-slate-400">Carregando...</div>
          ) : rows.length === 0 ? (
            <div className="p-12 text-center text-sm text-slate-400">
              Nenhum cliente ainda. Adicione um pré-cadastro ou aguarde um agendamento.
            </div>
          ) : (
            <table className="w-full text-sm min-w-[720px]">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold border-b border-slate-100">
                  <th className="text-left px-6 py-3.5 w-[45%]">Cliente</th>
                  <th className="text-left px-6 py-3.5 w-[15%]">Tipo</th>
                  <th className="text-left px-6 py-3.5 w-[15%]">Status</th>
                  <th className="text-right px-6 py-3.5 w-[25%]">Ações</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const isExpired = r.kind === 'invite' && (r.status === 'EXPIRED' || new Date(r.expiresAt) < new Date());
                  const dimmed = isExpired;
                  const tipoLabel = r.kind === 'registered' ? 'Cadastrado'
                    : r.status === 'ACCEPTED' ? 'Aceito'
                    : isExpired ? 'Expirado'
                    : 'Pendente';
                  const ativoBadge = r.kind === 'registered' || (r.kind === 'invite' && r.status === 'ACCEPTED');

                  return (
                    <tr
                      key={r.kind === 'registered' ? `reg-${r.id}` : `inv-${r.invId}`}
                      className={cn('hover:bg-slate-50/60 transition border-b border-slate-100 last:border-0', dimmed && 'opacity-60')}
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3 min-w-0">
                          {r.kind === 'registered' && r.image ? (
                            <img src={r.image} alt="" className="size-9 rounded-full object-cover shrink-0" />
                          ) : (
                            <div className="size-9 rounded-full bg-gradient-to-br from-teal-300 to-coral-300 flex items-center justify-center text-white text-xs font-bold shrink-0">
                              {r.name.charAt(0).toUpperCase()}
                            </div>
                          )}
                          <div className="min-w-0">
                            <div className="font-semibold text-slate-800 truncate">{r.name}</div>
                            <div className="text-[11px] text-slate-400 truncate">{r.email}</div>
                            {r.phone && <div className="text-[11px] text-slate-400">{r.phone}</div>}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <Badge>{tipoLabel}</Badge>
                      </td>
                      <td className="px-6 py-4">
                        {ativoBadge ? (
                          <span className="text-[10px] px-2 py-0.5 rounded border bg-teal-50 text-teal-700 border-teal-200 whitespace-nowrap">Ativo</span>
                        ) : (
                          <span className="text-[10px] px-2 py-0.5 rounded border bg-amber-50 text-amber-700 border-amber-200 whitespace-nowrap">
                            {isExpired ? 'Expirado' : 'Aguardando'}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2 flex-wrap justify-end">
                          {r.kind === 'registered' && (
                            <>
                              <Link
                                to="/dashboard/prontuarios/$clientId"
                                params={{ clientId: r.id }}
                                className="inline-flex items-center h-7 text-xs px-2.5 border border-slate-200 rounded-md text-slate-700 hover:bg-slate-50 hover:border-teal-300 hover:text-teal-700 transition"
                              >
                                Prontuário
                              </Link>
                              <Button size="sm" variant="outline" className="h-7 text-xs px-2.5"
                                onClick={() => setBookingFor({ id: r.id, name: r.name })}>
                                Agendar sessão
                              </Button>
                              <Link
                                to="/dashboard/sessoes/recorrencia/nova"
                                search={{ clientId: r.id } as any}
                                className="inline-flex items-center h-7 text-xs px-2.5 border border-slate-200 rounded-md text-slate-700 hover:bg-slate-50 hover:border-teal-300 hover:text-teal-700 transition"
                              >
                                Recorrência
                              </Link>
                            </>
                          )}
                          {r.kind === 'invite' && r.status === 'ACCEPTED' && invClientProfileId[r.invId] && (
                            <>
                              <Link
                                to="/dashboard/prontuarios/$clientId"
                                params={{ clientId: invClientProfileId[r.invId] }}
                                className="inline-flex items-center h-7 text-xs px-2.5 border border-slate-200 rounded-md text-slate-700 hover:bg-slate-50 hover:border-teal-300 hover:text-teal-700 transition"
                              >
                                Prontuário
                              </Link>
                              <Button size="sm" variant="outline" className="h-7 text-xs px-2.5"
                                onClick={() => setBookingFor({ id: invClientProfileId[r.invId], name: r.name })}>
                                Agendar sessão
                              </Button>
                              <Link
                                to="/dashboard/sessoes/recorrencia/nova"
                                search={{ clientId: invClientProfileId[r.invId] } as any}
                                className="inline-flex items-center h-7 text-xs px-2.5 border border-slate-200 rounded-md text-slate-700 hover:bg-slate-50 hover:border-teal-300 hover:text-teal-700 transition"
                              >
                                Recorrência
                              </Link>
                            </>
                          )}
                          {r.kind === 'invite' && r.status === 'PENDING' && !isExpired && (
                            <>
                              <Button size="sm" variant="outline" className="h-7 text-xs px-2.5"
                                onClick={() => {
                                  const url = `${window.location.origin}/convite/${r.token}`;
                                  navigator.clipboard.writeText(url);
                                  setCopiedToken(r.token);
                                  setTimeout(() => setCopiedToken(null), 2000);
                                }}>
                                {copiedToken === r.token ? 'Copiado!' : 'Copiar link'}
                              </Button>
                              <Button size="sm" variant="outline" className="h-7 text-xs px-2.5"
                                disabled={resendEmail.isPending}
                                onClick={() => resendEmail.mutate(r.invId)}>
                                {resendNotice?.id === r.invId
                                  ? (resendNotice.ok ? '✓ Enviado' : 'Erro')
                                  : 'Reenviar e-mail'}
                              </Button>
                            </>
                          )}
                          {r.kind === 'invite' && (
                            <Button size="sm" variant="ghost" className="h-7 text-xs px-2 text-red-500 hover:text-red-700 hover:bg-red-50"
                              disabled={removeInvite.isPending}
                              onClick={() => {
                                if (confirm(`Remover convite de ${r.name}?`)) removeInvite.mutate(r.invId);
                              }}>
                              Remover
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {bookingFor && myProfile?.id && (
        <BookSessionModal
          therapistId={myProfile.id}
          clientId={bookingFor.id}
          clientName={bookingFor.name}
          externalBillingAllowedUntil={myProfile.externalBillingAllowedUntil}
          onClose={() => setBookingFor(null)}
        />
      )}
      <style>{`.input{width:100%;background:white;border:1px solid rgb(226 232 240);border-radius:6px;height:38px;padding:0 12px;outline:none;font-size:14px}.input:focus{box-shadow:0 0 0 2px rgb(112 208 192)}`}</style>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-slate-700">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

function Badge({ children }: { children: React.ReactNode }) {
  const text = String(children);
  const cls =
    text === 'Cadastrado' || text === 'Aceito' ? 'bg-teal-50 text-teal-700 border-teal-200' :
    text === 'Expirado' ? 'bg-red-50 text-red-600 border-red-200' :
    'bg-amber-50 text-amber-700 border-amber-200';
  return <span className={cn('text-[10px] px-2 py-0.5 rounded border whitespace-nowrap', cls)}>{children}</span>;
}
